import React, { useState, useEffect, lazy, Suspense } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, Box, Image } from "lucide-react";

const ThreeDViewer = lazy(() =>
  import("./ThreeDViewer").then((mod) => ({ default: mod.ThreeDViewer }))
);

export const ImageLightbox = ({ images = [], startIndex = 0, open, onClose, model3d = "" }) => {
  const [current, setCurrent] = useState(startIndex);
  const [viewMode, setViewMode] = useState("images"); // "images" | "3d"

  useEffect(() => {
    if (open) {
      setCurrent(startIndex);
      setViewMode("images");
    }
  }, [open, startIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (viewMode === "images") {
        if (e.key === "ArrowLeft") setCurrent((c) => (c === 0 ? images.length - 1 : c - 1));
        if (e.key === "ArrowRight") setCurrent((c) => (c === images.length - 1 ? 0 : c + 1));
      }
    };
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, images.length, onClose, viewMode]);

  if (!open || (images.length === 0 && !model3d)) return null;

  const has3d = !!model3d;

  const prev = (e) => {
    e.stopPropagation();
    setCurrent((c) => (c === 0 ? images.length - 1 : c - 1));
  };

  const next = (e) => {
    e.stopPropagation();
    setCurrent((c) => (c === images.length - 1 ? 0 : c + 1));
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center animate-lightbox-in"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      {/* View mode toggle (Images / 3D) */}
      {has3d && images.length > 0 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex bg-white/10 backdrop-blur-md rounded-full p-1">
          <button
            onClick={(e) => { e.stopPropagation(); setViewMode("images"); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              viewMode === "images"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-white/70 hover:text-white"
            }`}
          >
            <Image className="w-4 h-4" />
            Photos
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setViewMode("3d"); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              viewMode === "3d"
                ? "bg-sky-500 text-white shadow-sm"
                : "text-white/70 hover:text-white"
            }`}
          >
            <Box className="w-4 h-4" />
            3D View
          </button>
        </div>
      )}

      {/* Image counter (only in image mode) */}
      {viewMode === "images" && images.length > 1 && !has3d && (
        <div className="absolute top-5 left-1/2 -translate-x-1/2 z-10 text-white/70 text-sm font-medium">
          {current + 1} / {images.length}
        </div>
      )}

      {/* Content area */}
      {viewMode === "images" ? (
        <>
          {/* Main image */}
          <div
            className="relative z-10 max-w-[90vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              key={current}
              src={images[current]}
              alt={`Preview ${current + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl animate-lightbox-img"
            />
          </div>

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={next}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </>
          )}

          {/* Thumbnail strip */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2 max-w-[80vw] overflow-x-auto py-2 px-3 rounded-xl bg-black/30 backdrop-blur-md">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setCurrent(idx); }}
                  className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                    idx === current
                      ? "border-white shadow-lg scale-105"
                      : "border-transparent opacity-50 hover:opacity-80"
                  }`}
                >
                  <img src={img} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        /* 3D Viewer */
        <div
          className="relative z-10 w-[85vw] h-[80vh] max-w-5xl rounded-xl overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <Suspense
            fallback={
              <div className="w-full h-full bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-10 h-10 border-2 border-sky-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">Loading 3D Viewer...</p>
                </div>
              </div>
            }
          >
            <ThreeDViewer modelUrl={model3d} className="w-full h-full" />
          </Suspense>
        </div>
      )}

      {/* Quick 3D toggle hint if only 3D (no images) */}
      {has3d && images.length === 0 && viewMode === "images" && (
        <div className="absolute z-10 text-center" onClick={(e) => e.stopPropagation()}>
          <p className="text-white/50 text-sm mb-3">No images available</p>
          <button
            onClick={() => setViewMode("3d")}
            className="px-5 py-2.5 rounded-full bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <Box className="w-4 h-4" />
            View in 3D
          </button>
        </div>
      )}
    </div>
  );
};

// Clickable image wrapper with zoom hint
export const ClickableImage = ({ src, alt, className, onClick, children }) => {
  return (
    <div className={`relative cursor-pointer group/zoom ${className || ""}`} onClick={onClick}>
      {children}
      <div className="absolute inset-0 bg-black/0 group-hover/zoom:bg-black/10 transition-colors duration-200 flex items-center justify-center pointer-events-none">
        <div className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover/zoom:opacity-100 transition-all duration-300 scale-75 group-hover/zoom:scale-100 shadow-lg">
          <ZoomIn className="w-5 h-5 text-slate-700" />
        </div>
      </div>
    </div>
  );
};
